import React, { useEffect } from 'react'
import styled, { css } from 'styled-components/macro'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from '../../../../hooks/useSearchParams'
import { Positioning, PositioningValues } from '../../Model/aaModel'
import FilterIcon from './FilterIcon'
import {
  breakpoints,
  getFluidSizeWithFullFormula as gF,
  palette,
  pxToRem,
} from '../../../../style/theme'
import { useDispatch, useSelector } from 'react-redux'
import assortmentAdvisorActions from '../../../../store/assortmentAdvisor/actions'
import { useGetBrandLogo } from '../../../../services/afaContents'
import classnames from 'classnames'
import { cartProductsFilteredByActiveDoorsSelector } from '../../../../store/cart/selectors'
import { useGetMapTrendsQuery, useGetTrendsAssortmentMutation } from '../../services'
import assortmentAdvisorSelectors from '../../../../store/assortmentAdvisor/selectors'
import { getDeviceFromUrl } from '../../../../libs/url'
import AreaTrendsButton from '../../Components/AreaTrendsButton'
import useSetDefaultPositionParam from '../../../../hooks/useSetPositionParam'

const pointerEvents = css`
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`

const IconButton = styled.div`
  cursor: pointer;
  display: flex;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: ${pxToRem(18)}rem;
  padding: ${gF('px', 10, 25, 1366, 3840)};
  justify-content: center;
  align-items: center;

  & > span:first-of-type {
    border-radius: ${pxToRem(14)}rem;
    background-color: ${palette.white};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${gF('px', 6, 12, 1366, 3840)};
  }

  & > span:last-of-type {
    color: ${palette.white};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 ${gF('px', 23, 46, 1366, 3840)};
    font-size: ${gF('px', 16, 32, 1366, 3840)};
  }
`

const BrandLogo = styled.div`
  background-color: ${palette.white};
  padding: ${gF('px', 8, 16, 1366, 3840)};
  border-radius: ${pxToRem(20)}rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: ${gF('px', 62, 124, 1366, 3840)};
  width: ${gF('px', 119, 238, 1366, 3840)};
  padding: ${gF('px', 16, 32, 1366, 3840)};

  img {
    height: max(4vh, 25px);
    max-height: 100%;
  }
`

const BrandsEdit = styled.span`
  text-decoration: underline;
  color: ${palette.cornflowerBlueAA};
  font-size: ${gF('px', 16, 32, 1366, 3840)};
  cursor: pointer;
`

const Toolbar = styled.div`
  position: absolute;
  top: ${gF('px', 18, 36, 1366, 3840)};
  left: ${gF('px', 18, 36, 1366, 3840)};
  width: calc(100% - ${gF('px', 36, 72, 1366, 3840)});
  ${pointerEvents}
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${gF('px', 8, 16, 1366, 3840)};
  ${pointerEvents};
`

const Overview = styled.div`
  border-radius: 1vw;
  background-color: ${palette.blueZodiacAA};
  display: flex;
  align-items: center;
  padding: ${gF('px', 16, 32, 1366, 3840)} ${gF('px', 26, 52, 1366, 3840)};
  gap: ${gF('px', 114, 228, 1366, 3840)};
  color: white;

  @media (max-width: ${breakpoints.L}) {
    height: ${pxToRem(121)}rem;
  }
`

const TrendsChartsContainer = styled.div`
  float: right;
`

const TrendsChartWrapper = styled.div`
  background-color: ${palette.blueZodiacAA};
  color: ${palette.white};
  border-radius: ${gF('px', 22, 44, 1366, 3840)};
  margin: ${gF('px', 8, 16, 1366, 3840)} 0;
  padding: ${gF('px', 20, 40, 1366, 3840)} ${gF('px', 28, 56, 1366, 3840)};
  width: ${gF('px', 385, 770, 1366, 3840)};
  position: relative;
  z-index: 999;

  &:first-of-type {
    margin-top: 0;
  }

  h2 {
    font-size: ${gF('px', 24, 48, 1366, 3840)};
    line-height: ${gF('px', 24, 48, 1366, 3840)};
    font-weight: ${({ theme }) => theme.typography.fontWeights.light};
  }
`

const TrendsChartLinesContainer = styled.div`
  margin-top: ${gF('px', 20, 40, 1366, 3840)};
`

const TrendsChartsLine = styled.div`
  margin: ${gF('px', 5, 10, 1366, 3840)} 0;
  height: ${gF('px', 19, 38, 1366, 3840)};
  font-size: ${gF('px', 15, 30, 1366, 3840)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  width: 100%;
  display: flex;
  align-items: center;

  > div {
    float: left;
  }

  .name {
    width: 35%;
    text-align: left;
    text-transform: capitalize;
  }

  .bar {
    width: 50%;
    height: ${gF('px', 6, 12, 1366, 3840)};
    border-radius: ${gF('px', 3, 3, 1366, 3840)};
    overflow: hidden;
    position: relative;

    div {
      position: absolute;
      height: 100%;
      border-radius: inherit;
      width: 0;
      transition: 0.25s width ease-in-out;

      &.young {
        background-color: ${palette.cornflowerBlueAA};
      }

      &.innovative {
        background-color: ${palette.seance};
      }

      &.sophisticated {
        background-color: ${palette.romanCoffee};
      }

      &.sport {
        background-color: ${palette.waterGreen};
      }

      &.everyday {
        background-color: ${palette.acidGreen};
      }
    }
  }

  .value {
    width: 15%;
    text-align: right;
  }
`

const ControlsLabel = styled.div`
  font-size: ${gF('px', 28, 56, 1366, 3840)};
  width: min-content;
  line-height: 1;
  font-weight: ${({ theme }) => theme.typography.fontWeights.light};
`

const Buttons = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  display: flex;
  align-items: center;
  gap: 1vw;
  font-size: ${gF('px', 15, 30, 1366, 3840)};
`

const Button = styled.button`
  padding: 0.5vw 1.2vw;
  color: var(--color);
  border-radius: ${gF('px', 12, 24, 1366, 3840)};
  height: ${gF('px', 43, 86, 1366, 3840)};
  text-transform: capitalize;
  background-color: var(--background-color);

  &.active {
    background-color: var(--color);
    color: white;
  }

  &.young {
    --color: ${palette.cornflowerBlueAA};
    --background-color: ${palette.cornflowerBlueAA}10;
  }

  &.innovative {
    --color: ${palette.barney};
    --background-color: ${palette.cornflowerBlueAA}10;

    &.active {
      --color: ${palette.seance};
      --background-color: ${palette.seance}10;
    }
  }

  &.sophisticated {
    --color: ${palette.romanCoffee};
    --background-color: ${palette.romanCoffee}10;
  }

  &.sport {
    --color: ${palette.waterGreen};
    --background-color: ${palette.waterGreen}10;
  }

  &.everyday {
    --color: ${palette.acidGreen};
    --background-color: ${palette.acidGreen}10;
  }
`

const ControlsLabelDemographics = styled.div`
  font-size: 2vw;
  width: min-content;
  line-height: 1;
  white-space: pre-wrap;
`

const ButtonsDemographics = styled.div`
  display: flex;
  align-items: center;
  gap: 1vw;
  font-size: 0.75vw;
`

const demographicsButtons = css`
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  border-radius: 14px;
  font-size: ${gF('px', 17, 34, 1366, 3840)};
  letter-spacing: -0.28px;
  background-color: ${palette.bigStoneAA};
  display: flex;
  align-items: center;
  text-transform: capitalize;
  cursor: pointer;

  &.selected {
    background-color: ${palette.white};
    color: ${palette.swamp};
  }
`

const AgeButton = styled.div`
  ${demographicsButtons};
  padding-top: ${gF('px', 10, 20, 1366, 3840)};
  padding-bottom: ${gF('px', 9, 18, 1366, 3840)};
  padding-left: ${gF('px', 9, 18, 1366, 3840)};
  padding-right: ${gF('px', 38, 76, 1366, 3840)};
  column-gap: ${gF('px', 18, 36, 1366, 3840)};
`

const IncomeButton = styled.div`
  ${demographicsButtons};
  padding-top: ${gF('px', 10, 20, 1366, 3840)};
  padding-bottom: ${gF('px', 9, 18, 1366, 3840)};
  padding-left: ${gF('px', 11, 22, 1366, 3840)};
  padding-right: ${gF('px', 19.5, 39, 1366, 3840)};
  column-gap: ${gF('px', 7.5, 15, 1366, 3840)};
`

const iconsCss = css<{ toggle: 'age' | 'income' }>`
  background-repeat: no-repeat;
  background-size: contain;
  width: ${gF('px', 29, 58, 1366, 3840)};
  height: ${gF('px', 29, 58, 1366, 3840)};
  filter: ${({ toggle }) => (toggle === 'age' ? 'invert(0)' : 'invert(1)')};
`

const AgeIcon = styled.div`
  background-image: url("data:image/svg+xml,%3Csvg width='29' height='29' viewBox='0 0 29 29' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='currentColor' fill-rule='evenodd'%3E%3Cpath d='M7.789 15.452c.353.223.74.335 1.16.335.42 0 .807-.113 1.16-.338a2.49 2.49 0 0 0 .847-.911c.212-.383.318-.815.318-1.296 0-.47-.107-.891-.32-1.264a2.443 2.443 0 0 0-.85-.883 2.168 2.168 0 0 0-1.155-.324c-.417 0-.803.11-1.157.33a2.499 2.499 0 0 0-1.173 2.153c0 .476.106.906.32 1.289.214.383.497.686.85.909M11.013 18.704c.271-.37.59-.698.954-.986a5.39 5.39 0 0 0-1.308-.652c-.497-.173-1.07-.26-1.72-.26-.755 0-1.434.122-2.039.366a4.993 4.993 0 0 0-1.548.96 4.34 4.34 0 0 0-.983 1.308 3.233 3.233 0 0 0-.341 1.404c0 .298.088.524.263.677.175.153.461.229.857.229h5.292c-.178-.216-.267-.493-.269-.83-.002-.336.07-.697.217-1.083.146-.386.354-.764.625-1.133M24.667 19.396a5.036 5.036 0 0 0-.894-1.335 6.747 6.747 0 0 0-1.428-1.173 7.491 7.491 0 0 0-1.912-.832 8.47 8.47 0 0 0-2.345-.308c-.848 0-1.63.102-2.344.308a7.49 7.49 0 0 0-1.912.832 6.67 6.67 0 0 0-1.425 1.173c-.39.431-.689.876-.894 1.335-.206.459-.309.896-.309 1.31 0 .357.125.62.376.79.25.17.67.254 1.26.254h10.497c.59 0 1.01-.085 1.26-.255.25-.17.375-.432.375-.788 0-.415-.102-.852-.305-1.31M16.469 13.818a2.969 2.969 0 0 0 1.626.471c.589 0 1.131-.157 1.625-.471.494-.314.89-.74 1.19-1.279.299-.538.448-1.145.448-1.82a3.545 3.545 0 0 0-.973-2.47 3.3 3.3 0 0 0-1.04-.735 3.019 3.019 0 0 0-1.25-.264c-.437 0-.853.09-1.247.267a3.308 3.308 0 0 0-1.044.742 3.5 3.5 0 0 0-.71 1.11c-.171.422-.257.88-.257 1.37 0 .666.149 1.267.445 1.803.297.537.693.962 1.187 1.276'/%3E%3C/g%3E%3C/svg%3E%0A");
  ${iconsCss}
`

const IncomeIcon = styled.div`
  background-image: url("data:image/svg+xml,%3Csvg width='29' height='29' viewBox='0 0 29 29' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath id='iuq4f9jxaa' d='M0 0h21.75v21.75H0z'/%3E%3C/defs%3E%3Cg fill='currentColor' fill-rule='evenodd'%3E%3Cg transform='translate(4.028 4.028)'%3E%3Cmask id='l73d159chb' fill='%23fff'%3E%3Cuse xlink:href='%23iuq4f9jxaa'/%3E%3C/mask%3E%3Cpath d='M19.721 13.822a9.32 9.32 0 0 1-1.32 2.564 9.24 9.24 0 0 1-2.015 2.016 9.412 9.412 0 0 1-2.564 1.323 9.177 9.177 0 0 1-2.951.472 9.15 9.15 0 0 1-2.943-.472 9.345 9.345 0 0 1-4.576-3.34 9.25 9.25 0 0 1-1.79-5.506 9.24 9.24 0 0 1 1.786-5.514A9.277 9.277 0 0 1 7.92 2.029a9.22 9.22 0 0 1 2.943-.468 9.249 9.249 0 0 1 5.515 1.787 9.322 9.322 0 0 1 3.344 4.58c.312.932.468 1.916.468 2.951 0 1.03-.156 2.011-.468 2.943m1.474-6.354a10.952 10.952 0 0 0-1.561-2.984 11.239 11.239 0 0 0-2.368-2.367A10.932 10.932 0 0 0 14.277.555 10.506 10.506 0 0 0 10.862 0c-1.19 0-2.328.185-3.41.555-1.083.37-2.076.89-2.98 1.562a11.28 11.28 0 0 0-2.364 2.367C1.438 5.39.918 6.386.551 7.468A10.543 10.543 0 0 0 0 10.88c0 1.186.185 2.321.555 3.407a11.064 11.064 0 0 0 6.905 6.909c1.083.37 2.22.555 3.41.555 1.192 0 2.329-.185 3.412-.555 1.082-.37 2.077-.89 2.984-1.557a11.153 11.153 0 0 0 2.368-2.363 10.936 10.936 0 0 0 1.56-2.99c.37-1.085.556-2.22.556-3.406 0-1.19-.185-2.328-.555-3.41' fill='%23FFF' mask='url(%23l73d159chb)'/%3E%3C/g%3E%3Cpath d='M16.698 18.163c-.309.284-.756.454-1.34.51V15.55l.234.058c.567.15.97.347 1.21.589s.36.547.36.914c0 .418-.155.768-.464 1.052zm-2.259-4-.075-.024c-.529-.15-.92-.34-1.173-.568a1.117 1.117 0 0 1-.38-.868c0-.368.15-.686.447-.956.298-.27.692-.439 1.181-.505v2.922zm1.11.268-.191-.033v-3.156c.428.06.79.214 1.085.459.295.245.49.56.585.943.105.318.3.476.584.476a.536.536 0 0 0 .397-.154.547.547 0 0 0 .154-.405.96.96 0 0 0-.012-.159 4.893 4.893 0 0 0-.03-.158c-.139-.574-.46-1.048-.964-1.424-.504-.376-1.103-.6-1.8-.672v-.743a.437.437 0 0 0-.133-.334.47.47 0 0 0-.334-.126.44.44 0 0 0-.325.126.452.452 0 0 0-.126.334v.743c-.54.056-1.018.199-1.436.43a2.744 2.744 0 0 0-.99.902c-.241.37-.362.794-.362 1.273 0 .657.225 1.192.676 1.607.45.415 1.122.725 2.012.931l.1.025v3.356c-.523-.066-.946-.235-1.269-.505a1.707 1.707 0 0 1-.6-1.014.76.76 0 0 0-.218-.363.535.535 0 0 0-.359-.121.53.53 0 0 0-.397.158.562.562 0 0 0-.154.41c0 .055.004.108.012.158.009.05.018.103.03.159.15.618.497 1.12 1.04 1.507.542.387 1.18.613 1.915.68v.802c0 .139.043.25.13.334a.444.444 0 0 0 .321.125.47.47 0 0 0 .334-.125.437.437 0 0 0 .134-.334v-.785c.846-.072 1.551-.345 2.116-.818.565-.473.848-1.102.848-1.887 0-.707-.231-1.276-.693-1.708-.462-.431-1.155-.736-2.08-.914z' fill='%23FFF'/%3E%3C/g%3E%3C/svg%3E%0A");
  ${iconsCss}
`

type Trend = {
  label: string
  value: number
}

type Props = {
  title: string
  trends: Trend[]
}
const TrendsChart: React.FC<Props> = ({ title, trends }) => {
  const { t } = useTranslation()
  const trendsSorting = ['young', 'innovative', 'sophisticated', 'sport', 'everyday']
  const noData = trends.length === 0 || trends.every(({ value }) => value === 0)

  return (
    <TrendsChartWrapper>
      <h2>{title}</h2>
      <TrendsChartLinesContainer>
        {noData && <h4>No data available</h4>}
        {!noData &&
          trends
            .slice()
            .sort((a, b) => {
              return trendsSorting.indexOf(a.label) - trendsSorting.indexOf(b.label)
            })
            .map(({ label, value }) => (
              <TrendsChartsLine key={label}>
                <div className="name">{t(`AA.${label}`)}</div>
                <div className="bar">
                  <div style={{ width: `${value}%` }} className={classnames({ [label]: true })} />
                </div>
                <div className="value">{value}%</div>
              </TrendsChartsLine>
            ))}
      </TrendsChartLinesContainer>
    </TrendsChartWrapper>
  )
}

type MapProps = {
  setDemographicsToggle: React.Dispatch<React.SetStateAction<'age' | 'income'>>
  demographicsToggle: 'age' | 'income'
}

const MapControls: React.FC<MapProps> = ({ setDemographicsToggle, demographicsToggle }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const mapView = searchParams.get('mapView') || 'trends'
  const selectedPositioning = searchParams.get('positioning') as Positioning | null
  const selectedBrandCode = searchParams.get('mapBrand') || 'All'
  const { brandLogo } = useGetBrandLogo(selectedBrandCode)
  const cartProductsFilteredByActiveDoors = useSelector(cartProductsFilteredByActiveDoorsSelector)
  const [getTrendsAssortment, productsQuery] = useGetTrendsAssortmentMutation()
  const device = getDeviceFromUrl()
  const isWall = device === 'wall'
  const trendsEnabled = searchParams.get('trends') === 'true'

  useEffect(() => {
    getTrendsAssortment(
      cartProductsFilteredByActiveDoors.reduce((result, { modelCode, colorCode }) => {
        if (!(modelCode in result)) {
          result[modelCode] = []
        }
        result[modelCode].push(colorCode)
        return result
      }, {} as Record<string, string[]>),
    )
  }, [cartProductsFilteredByActiveDoors, getTrendsAssortment])

  // Via Valcozzena, 10 Agordo
  const searchedAddress = useSelector(assortmentAdvisorSelectors.searchedAddressSelector)
  const { data } = useGetMapTrendsQuery({
    country: searchedAddress?.country.short || '',
    zipCode: searchedAddress?.zipCode || '',
  })
  const areaTrendsData = data || []
  const areaTrendsByPositioning = areaTrendsData.reduce(
    (result, { young, innovative, sophisticated, sport, everyday }) => {
      result.young += young
      result.innovative += innovative
      result.sport += sport
      result.everyday += everyday
      result.sophisticated += sophisticated
      return result
    },
    {
      young: 0,
      innovative: 0,
      sophisticated: 0,
      everyday: 0,
      sport: 0,
    } as Record<Positioning, number>,
  )

  const cartProductsFilteredByActiveDoorsDictionary = cartProductsFilteredByActiveDoors.reduce(
    (res, { modelCode, colorCode, quantity }) => {
      return {
        ...res,
        [`${modelCode} ${colorCode}`]: quantity,
      }
    },
    {} as Record<string, number>,
  )

  const o: Record<Positioning, Trend> = {
    everyday: { label: 'everyday', value: 0 },
    innovative: { label: 'innovative', value: 0 },
    sport: { label: 'sport', value: 0 },
    young: { label: 'young', value: 0 },
    sophisticated: { label: 'sophisticated', value: 0 },
  }

  const selectionTrendsData =
    productsQuery?.data?.reduce((res, product) => {
      PositioningValues.forEach(positioning => {
        o[positioning].value =
          o[positioning].value +
          product.positioning[positioning] *
            cartProductsFilteredByActiveDoorsDictionary[`${product.modelCode} ${product.colorCode}`]
      })
      return {
        ...res,
        ...o,
      }
    }, {} as Record<Positioning, Trend>) || {}

  const selectionTrendsDataValues: Trend[] = Object.values(selectionTrendsData)

  useSetDefaultPositionParam()

  const setSelectedPositioning = (positioning: Positioning) => {
    searchParams.set('positioning', positioning)
    setSearchParams(searchParams, { replace: true })
  }

  const selectedAreaTrendsTotal = Object.values(areaTrendsByPositioning).reduce(
    (sum, value) => sum + value,
    0,
  )
  const selectedAreaTrends: Trend[] = Object.entries(areaTrendsByPositioning).reduce(
    (result, [positioning, value]) => {
      result.push({
        label: positioning,
        value: Math.round((value * 100) / selectedAreaTrendsTotal) || 0,
      })
      return result
    },
    [] as Trend[],
  )

  const yourSelectionTrendsTotal = selectionTrendsDataValues.reduce((sum, el) => sum + el.value, 0)
  const yourSelectionTrends: Trend[] = selectionTrendsDataValues.reduce((res, el) => {
    return [
      ...res,
      {
        ...el,
        value: Math.round((el.value * 100) / yourSelectionTrendsTotal),
      },
    ]
  }, [] as Trend[])

  return (
    <Toolbar>
      <Controls>
        <Overview>
          {mapView === 'trends' && (
            <>
              <ControlsLabel>{t('AA.areaOverview')}</ControlsLabel>
              <Buttons>
                <Button
                  className={classnames({ young: true, active: selectedPositioning === 'young' })}
                  onClick={() => setSelectedPositioning('young')}
                >
                  {t('AA.young')}
                </Button>
                <Button
                  className={classnames({
                    innovative: true,
                    active: selectedPositioning === 'innovative',
                  })}
                  onClick={() => setSelectedPositioning('innovative')}
                >
                  {t('AA.innovative')}
                </Button>
                <Button
                  className={classnames({
                    sophisticated: true,
                    active: selectedPositioning === 'sophisticated',
                  })}
                  onClick={() => setSelectedPositioning('sophisticated')}
                >
                  {t('AA.sophisticated')}
                </Button>
                <Button
                  className={classnames({ sport: true, active: selectedPositioning === 'sport' })}
                  onClick={() => setSelectedPositioning('sport')}
                >
                  {t('AA.sport')}
                </Button>
                <Button
                  className={classnames({
                    everyday: true,
                    active: selectedPositioning === 'everyday',
                  })}
                  onClick={() => setSelectedPositioning('everyday')}
                >
                  {t('AA.everyday')}
                </Button>
              </Buttons>
            </>
          )}
          {mapView === 'brands' && (
            <>
              <ControlsLabel>{t('AA.Map.Brands.Title')}</ControlsLabel>
              <Buttons>
                {selectedBrandCode === 'All' ? (
                  <IconButton
                    onClick={() => dispatch(assortmentAdvisorActions.setMapBrandsFilterOpen(true))}
                  >
                    <span>
                      <FilterIcon />
                    </span>
                    <span>{t('AA.Map.Brands.Filter')}</span>
                  </IconButton>
                ) : (
                  <>
                    <BrandLogo>
                      <img src={brandLogo} alt="" />
                    </BrandLogo>
                    <BrandsEdit
                      onClick={() =>
                        dispatch(assortmentAdvisorActions.setMapBrandsFilterOpen(true))
                      }
                    >
                      {t('AA.Map.Brands.Edit')}
                    </BrandsEdit>
                  </>
                )}
              </Buttons>
            </>
          )}
          {mapView === 'demographics' && (
            <>
              <ControlsLabelDemographics>{t('AA.demographicOverview')}</ControlsLabelDemographics>
              <ButtonsDemographics>
                <AgeButton
                  onClick={() => setDemographicsToggle('age')}
                  className={`${demographicsToggle === 'age' ? 'selected' : ''}`.trim()}
                >
                  <AgeIcon toggle={demographicsToggle} /> <span>{t('AA.age')}</span>
                </AgeButton>
                <IncomeButton
                  onClick={() => setDemographicsToggle('income')}
                  className={`${demographicsToggle === 'income' ? 'selected' : ''}`.trim()}
                >
                  <IncomeIcon toggle={demographicsToggle} /> <span>{t('AA.income')}</span>
                </IncomeButton>
              </ButtonsDemographics>
            </>
          )}
        </Overview>
        {!isWall && mapView === 'trends' && <AreaTrendsButton mode="map" />}
      </Controls>
      {trendsEnabled && mapView === 'trends' && (
        <TrendsChartsContainer>
          <TrendsChart title={t('AA.selectedArea')} trends={selectedAreaTrends} />
          <TrendsChart title={t('AA.yourSelection')} trends={yourSelectionTrends} />
        </TrendsChartsContainer>
      )}
    </Toolbar>
  )
}

export default MapControls
